/* =====================================================================
   assets/soi-cart.js · Spirit of India · M2 cart drawer and cart page (04 §7, §8; 05 C32, §7.4)
   The maths lives server-side in snippets/soi-cart.liquid (one renderer, drawer and page always agree). This file:
   - keeps Studio's <cart-drawer> contract and extends it (focus on the close button, back gesture on S, no
     double renders, fresh focus trap after every render, synced is-empty);
   - steppers (optimistic, debounced, one /cart/update.js per burst), remove row, remove group (confirm row),
     set stepper (proportional), „Passt dazu" add, checkout loading state;
   - the family mini picker (sheet over the drawer body; <dialog> on the cart page) fed by
     /collections/all?view=soi-data (cached 10 minutes);
   - the live region, the free-shipping bar transition, express buttons.
   No frameworks; progressive enhancement (every control is a real form without JS); passive listeners.
   ===================================================================== */
(() => {
  'use strict';
  if (window.soiCart) return;

  const doc = document;
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const small = () => window.matchMedia('(max-width: 767.98px)').matches;
  const parse = (html) => new DOMParser().parseFromString(html, 'text/html');
  let i18nCache = null;
  const T = () => {
    if (i18nCache) return i18nCache;
    const el = doc.querySelector('[data-soi-cart-i18n]');
    try { i18nCache = el ? JSON.parse(el.textContent) : {}; } catch (e) { i18nCache = {}; }
    return i18nCache;
  };
  const fill = (s, map) => Object.keys(map).reduce((acc, k) => acc.split(`[${k}]`).join(map[k]), s || '');
  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const money = (cents) => {
    const neg = cents < 0;
    const c = Math.abs(Math.round(cents));
    const eur = String(Math.floor(c / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return `${neg ? '−' : ''}${eur},${String(c % 100).padStart(2, '0')} €`;
  };
  const unitCents = (cents, ml) => Math.floor((cents * 200 + ml) / (2 * ml));
  const drawerEl = () => doc.querySelector('cart-drawer');
  const pageHost = () => doc.querySelector('[data-soi-cart-host]');
  const routes = () => window.routes || {};

  /* ---------------------------------------------------------------- network */
  const sectionIds = () => {
    const ids = [];
    if (drawerEl()) ids.push('cart-drawer');
    ids.push('cart-icon-bubble');
    const host = pageHost();
    if (host && host.dataset.section) ids.push(host.dataset.section);
    return ids;
  };
  const post = async (url, body, withSections = true) => {
    if (!navigator.onLine) throw Object.assign(new Error('offline'), { offline: true });
    const payload = Object.assign({}, body);
    if (withSections) {
      payload.sections = sectionIds().join(',');
      payload.sections_url = window.location.pathname;
    }
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    });
    let data = null;
    try { data = await res.json(); } catch (e) { data = null; }
    if (!res.ok || !data || (data.status && data.status >= 400)) {
      throw Object.assign(new Error((data && (data.description || data.message)) || 'cart'), { status: res.status, data });
    }
    return data;
  };
  const updateUrl = () => `${routes().cart_update_url || '/cart/update'}.js`;
  const addUrl = () => `${routes().cart_add_url || '/cart/add'}.js`;

  /* ---------------------------------------------------------------- rendering */
  const barValue = (scope) => {
    const f = scope && scope.querySelector('[data-soi-bar-fill]');
    return f ? f.style.getPropertyValue('--p') : null;
  };
  const animateBar = (scope, prev) => {
    const f = scope && scope.querySelector('[data-soi-bar-fill]');
    if (!f || prev == null || reduceMotion()) return;
    const next = f.style.getPropertyValue('--p');
    if (next === prev) return;
    f.style.setProperty('--p', prev);
    f.getBoundingClientRect();
    requestAnimationFrame(() => f.style.setProperty('--p', next));
  };
  const focusKey = () => {
    const a = doc.activeElement;
    if (!a || a === doc.body) return null;
    const holder = a.closest('[data-soi-fid]');
    return holder ? holder.getAttribute('data-soi-fid') : null;
  };
  const restoreFocus = (scope, fid, fallback) => {
    let el = fid && scope ? scope.querySelector(`[data-soi-fid="${CSS.escape(fid)}"]`) : null;
    if (el && (el.disabled || el.closest('[hidden]'))) el = null;
    if (!el) el = fallback;
    if (!el) return;
    const drawer = drawerEl();
    if (drawer && drawer.classList.contains('active') && drawer.contains(el) && typeof window.trapFocus === 'function') {
      window.trapFocus(doc.getElementById('CartDrawer'), el);
    } else {
      el.focus({ preventScroll: true });
    }
  };

  // A section rendered by another theme (Studio markup, e.g. a stale preview session) would wipe the soi cart:
  // fall back to one GET render of the same sections through the current page's theme.
  const foreign = (sections) => {
    const d = sections['cart-drawer'];
    if (d && !d.includes('data-soi-cart')) return true;
    const bub = sections['cart-icon-bubble'];
    if (bub && !bub.includes('soi-icon')) return true;
    const host = pageHost();
    const h = host && sections[host.dataset.section];
    return !!(host && host.dataset.section && sections[host.dataset.section] !== undefined && (!h || !h.includes('data-soi-cart')));
  };
  const applySections = (sections, opts = {}) => {
    if (!sections) return;
    if (foreign(sections)) {
      if (!opts.refetched) return refreshAll(Object.assign({}, opts, { refetched: true }));
      doc.querySelectorAll('[data-soi-cart] .is-busy, [data-soi-cart] .is-pending').forEach((el) => el.classList.remove('is-busy', 'is-pending'));
      return Promise.resolve();
    }
    // focus is read before the swap in every mode: a keyboard user inside the open drawer is never dropped to <body>
    const fidNow = focusKey();
    const fid = opts.focus === false ? null : fidNow;
    const prevId = doc.activeElement && doc.activeElement !== doc.body ? doc.activeElement.id : '';
    const lost = !doc.activeElement || doc.activeElement === doc.body;
    const focusInDrawer = !!(drawerEl() && (drawerEl().contains(doc.activeElement) || (lost && drawerEl().classList.contains('active'))));
    const focusInPage = !!(pageHost() && pageHost().contains(doc.activeElement));
    const drawerRoot = doc.getElementById('CartDrawer');
    if (drawerRoot && sections['cart-drawer']) {
      const src = parse(sections['cart-drawer']).querySelector('#CartDrawer');
      if (src) {
        const prev = barValue(drawerRoot);
        const scrollBody = drawerRoot.querySelector('[data-soi-cart-body]');
        const top = scrollBody ? scrollBody.scrollTop : 0;
        drawerRoot.innerHTML = src.innerHTML;
        const nb = drawerRoot.querySelector('[data-soi-cart-body]');
        if (nb) nb.scrollTop = top;
        animateBar(drawerRoot, prev);
      }
    }
    const host = pageHost();
    if (host && sections[host.dataset.section]) {
      const src = parse(sections[host.dataset.section]).querySelector('[data-soi-cart-host]');
      if (src) {
        const prev = barValue(host);
        host.innerHTML = src.innerHTML;
        animateBar(host, prev);
        if (window.customElements && host.querySelector('soi-carousel')) {
          /* carousels re-upgrade automatically on insertion */
        }
      }
    }
    if (sections['cart-icon-bubble']) {
      const bubble = doc.getElementById('cart-icon-bubble');
      const inner = parse(sections['cart-icon-bubble']).querySelector('.shopify-section');
      if (bubble && inner) bubble.innerHTML = inner.innerHTML;
    }
    afterRender({ announce: opts.announce !== false });
    if (opts.focus === false) {
      // background refresh (refetch, pub/sub, add paths): only repair focus that the swap removed from the drawer
      const d = drawerEl();
      const a = doc.activeElement;
      if (focusInDrawer && d && d.classList.contains('active') && (!a || a === doc.body || !d.contains(a))) {
        // same control (fid), else the same element by id (e.g. a group title focused after a picker add), else close
        let same = prevId ? doc.getElementById(prevId) : null;
        if (same && !d.contains(same)) same = null;
        if (same && same.tabIndex < 0 && !same.hasAttribute('tabindex')) same.setAttribute('tabindex', '-1');
        restoreFocus(doc.getElementById('CartDrawer'), fidNow, same || doc.querySelector('#CartDrawer [data-soi-cart-close]'));
      }
      return Promise.resolve();
    }
    if (focusInDrawer && drawerEl()) {
      restoreFocus(doc.getElementById('CartDrawer'), fid, doc.querySelector('#CartDrawer [data-soi-cart-close]'));
    } else if (focusInPage && pageHost()) {
      restoreFocus(pageHost(), fid, pageHost().querySelector('.soi-cpage__title'));
    }
    return Promise.resolve();
  };

  const liveRegion = () => {
    let r = doc.querySelector('[data-soi-cart-live]');
    if (!r) {
      r = doc.createElement('div');
      r.className = 'soi-sr';
      r.setAttribute('role', 'status');
      r.setAttribute('aria-live', 'polite');
      r.setAttribute('data-soi-cart-live', '');
      doc.body.append(r);
    }
    return r;
  };
  const announce = (msg) => {
    if (!msg) return;
    const r = liveRegion();
    r.textContent = '';
    setTimeout(() => { r.textContent = msg; }, 60);
  };

  const welcomeSeen = () => {
    try {
      const v = localStorage.getItem('soi_popup_state') || sessionStorage.getItem('soi_popup_state') || localStorage.getItem('soi-popup') || '';
      return /dismiss|done|submit|success|closed/i.test(v);
    } catch (e) { return false; }
  };

  // Express wallets (F-R1-G-13): Shopify's loader fetches portable-wallets as soon as init() runs, so it only runs
  // when a wallet element exists. The drawer keeps its wallets in a <template> until the drawer is open.
  const initWallets = () => {
    if (!doc.querySelector('shopify-accelerated-checkout-cart, shopify-accelerated-checkout')) return;
    if (window.Shopify && window.Shopify.PaymentButton && typeof window.Shopify.PaymentButton.init === 'function') {
      try { window.Shopify.PaymentButton.init(); } catch (e) { /* wallets render on the next page view */ }
    }
  };
  const injectExpress = (drawer) => {
    const slot = drawer && drawer.querySelector('[data-soi-express-slot]');
    const tpl = drawer && drawer.querySelector('template[data-soi-express-tpl]');
    if (!slot || !tpl || slot.childElementCount) return;
    slot.append(tpl.content.cloneNode(true));
  };
  // „Was gut dazu passt": arrows only when the track really scrolls (F-R1-G-33)
  let completeRO = null;
  const syncCompleteControls = () => {
    doc.querySelectorAll('.soi-ccomplete').forEach((sec) => {
      const track = sec.querySelector('[data-soi-track]');
      const ctrl = sec.querySelector('[data-soi-controls]');
      if (!track || !ctrl) return;
      const check = () => ctrl.classList.toggle('is-static', track.scrollWidth <= track.clientWidth + 1);
      check();
      if ('ResizeObserver' in window) {
        if (!completeRO) completeRO = new ResizeObserver((entries) => entries.forEach((en) => en.target.__soiCheck && en.target.__soiCheck()));
        track.__soiCheck = check;
        completeRO.observe(track);
      }
    });
  };

  const afterRender = (opts = {}) => {
    const drawer = drawerEl();
    const roots = [...doc.querySelectorAll('[data-soi-cart]')];
    const main = roots[0];
    if (drawer) {
      const dRoot = drawer.querySelector('[data-soi-cart]');
      const empty = dRoot ? Number(dRoot.dataset.count || 0) === 0 : true;
      drawer.classList.toggle('is-empty', empty);
    }
    
    if (opts.announce && main) announce(main.dataset.live);
    if (welcomeSeen()) doc.querySelectorAll('[data-soi-welcome-hint]').forEach((h) => { h.hidden = false; });
    if (drawer && drawer.classList.contains('active')) injectExpress(drawer);
    initWallets();
    syncCompleteControls();
    doc.dispatchEvent(new CustomEvent('soi:cart:rendered'));
  };

  // One GET per section (?section_id=, Studio's own refresh path). Used after external cart changes and as the
  // fallback when a POST answered with sections of another theme. Last resort: read the same nodes from a full
  // page render (a preview proxy can render section requests with the published theme).
  const pageFallback = async () => {
    const res = await fetch(window.location.href, { credentials: 'same-origin' });
    if (!res.ok) return {};
    const d = parse(await res.text());
    const out = {};
    const cd = d.querySelector('cart-drawer');
    if (cd) out['cart-drawer'] = `<div class="shopify-section">${cd.outerHTML}</div>`;
    const host = pageHost();
    const ph = d.querySelector('[data-soi-cart-host]');
    if (host && ph) out[host.dataset.section] = `<div class="shopify-section">${ph.outerHTML}</div>`;
    const bub = d.getElementById('cart-icon-bubble');
    if (bub) out['cart-icon-bubble'] = `<div class="shopify-section">${bub.innerHTML}</div>`;
    return out;
  };
  const refreshAll = async (opts = { announce: false, focus: false }) => {
    try {
      const ids = sectionIds();
      const htmls = await Promise.all(ids.map((id) => fetch(`${window.location.pathname}?section_id=${encodeURIComponent(id)}`)
        .then((r) => (r.ok ? r.text() : null)).catch(() => null)));
      let sections = {};
      ids.forEach((id, i) => { if (htmls[i]) sections[id] = htmls[i]; });
      if (foreign(sections)) sections = await pageFallback();
      await applySections(sections, Object.assign({ refetched: true }, opts));
    } catch (e) { /* keep the current render */ }
  };

  const publishUpdate = (data) => {
    try {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        // source 'cart-items' so Studio's CartItems subscribers do not fetch the same state again
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: data });
      }
    } catch (e) { /* no pubsub on this page */ }
  };

  /* ---------------------------------------------------------------- errors */
  const showError = (container, msg) => {
    if (!container) return;
    const el = container.querySelector('[data-soi-row-error]') || container.closest('[data-soi-row], .soi-cgroup, .soi-creco')?.querySelector('[data-soi-row-error]');
    if (!el) { if (window.soiToast) window.soiToast(msg, { type: 'error' }); return; }
    el.textContent = msg;
    el.hidden = false;
  };
  const errorText = (err, wanted) => {
    const t = T();
    if (err && err.offline) return t.offline || t.errorQty;
    if (err && err.status === 422) return t.errorStock || t.errorQty;
    return t.errorQty || '';
  };

  /* ---------------------------------------------------------------- updates (rows, sets, groups) */
  const parseLines = (s) => (s || '').split(',').filter(Boolean).map((p) => {
    const i = p.lastIndexOf(':');
    return { key: p.slice(0, i), qty: Number(p.slice(i + 1)) || 0 };
  });

  const runUpdate = async (updates, scopeEl) => {
    const scope = scopeEl && (scopeEl.closest('[data-soi-row]') || scopeEl.closest('.soi-cgroup') || scopeEl);
    if (scope) scope.classList.add('is-busy');
    try {
      const data = await post(updateUrl(), { updates });
      applySections(data.sections);
      publishUpdate(data);
      return data;
    } catch (err) {
      if (scope) scope.classList.remove('is-busy');
      throw err;
    }
  };

  const rowState = new WeakMap();
  const onStep = (btn) => {
    const row = btn.closest('[data-soi-row]');
    if (!row || row.classList.contains('is-busy')) return;
    const st = rowState.get(row) || { target: Number(row.dataset.qty) || 0, timer: null };
    const step = Number(btn.dataset.step) || 0;
    const max = Number(row.dataset.max) || 0;
    const errEl = row.querySelector('[data-soi-row-error]');
    if (errEl) errEl.hidden = true;
    if (step > 0 && max && st.target + step > max) {
      showError(row, T().errorStock);
      return;
    }
    st.target = Math.max(0, st.target + step);
    const val = row.querySelector('[data-soi-qty-val]');
    if (val) val.textContent = st.target;
    row.classList.add('is-pending');
    clearTimeout(st.timer);
    st.timer = setTimeout(() => commitRow(row, st), 380);
    rowState.set(row, st);
  };
  const commitRow = async (row, st) => {
    const lines = parseLines(row.dataset.lines);
    const current = lines.reduce((a, l) => a + l.qty, 0);
    let diff = st.target - current;
    if (!diff) { row.classList.remove('is-pending'); return; }
    const updates = {};
    if (diff > 0) {
      updates[lines[0].key] = lines[0].qty + diff;
    } else {
      for (const l of lines) {
        if (!diff) break;
        const take = Math.min(l.qty, -diff);
        updates[l.key] = l.qty - take;
        diff += take;
      }
    }
    const variant = row.dataset.variant;
    const wanted = st.target;
    try {
      const data = await runUpdate(updates, row);
      rowState.delete(row);
      const got = (data.items || []).filter((i) => String(i.variant_id) === String(variant)).reduce((a, i) => a + i.quantity, 0);
      if (got < wanted) {
        const fresh = doc.querySelectorAll(`[data-soi-row][data-variant="${variant}"]`);
        fresh.forEach((r) => showError(r, T().errorStock));
      }
    } catch (err) {
      rowState.delete(row);
      row.classList.remove('is-pending', 'is-busy');
      const val = row.querySelector('[data-soi-qty-val]');
      if (val) val.textContent = row.dataset.qty;
      showError(row, errorText(err, wanted));
    }
  };

  const onSetStep = async (btn) => {
    const box = btn.closest('[data-soi-setqty]');
    const group = btn.closest('.soi-cgroup');
    if (!box || (group && group.classList.contains('is-busy'))) return;
    const count = Number(box.dataset.count) || 1;
    const next = Math.max(0, count + (Number(btn.dataset.step) || 0));
    const val = box.querySelector('[data-soi-qty-val]');
    if (val) val.textContent = next;
    const updates = {};
    parseLines(box.dataset.lines).forEach((l) => { updates[l.key] = l.qty * next; });
    try {
      await runUpdate(updates, group);
    } catch (err) {
      if (val) val.textContent = count;
      showError(group, errorText(err));
    }
  };

  const formUpdates = (form) => {
    const updates = {};
    new FormData(form).forEach((v, k) => {
      const m = /^updates\[(.+)\]$/.exec(k);
      if (m) updates[m[1]] = Number(v) || 0;
    });
    return updates;
  };

  const confirmGroup = (form) => {
    if (form.querySelector('.soi-cconfirm')) return;
    const t = T();
    const btn = form.querySelector('button');
    const box = doc.createElement('span');
    box.className = 'soi-cconfirm';
    box.setAttribute('role', 'group');
    box.innerHTML = `<span class="soi-cconfirm__q">${esc(fill(t.confirm, { count: form.dataset.count || '' }))}</span>`
      + `<button type="button" class="soi-clink soi-clink--strong" data-confirm-yes>${esc(t.yes)}</button>`
      + `<button type="button" class="soi-clink" data-confirm-no>${esc(t.cancel)}</button>`;
    btn.hidden = true;
    form.append(box);
    const no = box.querySelector('[data-confirm-no]');
    const yes = box.querySelector('[data-confirm-yes]');
    no.addEventListener('click', () => { box.remove(); btn.hidden = false; btn.focus(); });
    yes.addEventListener('click', async () => {
      if (yes.getAttribute('aria-disabled') === 'true') return;
      yes.setAttribute('aria-disabled', 'true');
      try { await runUpdate(formUpdates(form), form); } catch (err) {
        yes.removeAttribute('aria-disabled');
        showError(form.closest('.soi-cgroup'), errorText(err));
      }
    });
    box.addEventListener('keydown', (e) => { if (e.key === 'Escape') { e.stopPropagation(); swallowKeyup = true; no.click(); } });
    no.focus();
  };

  /* ---------------------------------------------------------------- mini picker (04 §7.6) */
  const DATA_TTL = 10 * 60 * 1000;
  const loadData = async () => {
    const url = T().dataUrl || '/collections/all?view=soi-data';
    const key = `soi-cart-data:${url}`;
    try {
      const cached = JSON.parse(sessionStorage.getItem(key) || 'null');
      if (cached && Date.now() - cached.t < DATA_TTL) return cached.p;
    } catch (e) { /* no storage */ }
    const fetchPage = async (n) => {
      const res = await fetch(`${url}${url.includes('?') ? '&' : '?'}page=${n}`, { headers: { Accept: 'text/html' } });
      if (!res.ok) throw new Error('data');
      const el = parse(await res.text()).querySelector('[data-soi-cart-data]');
      return JSON.parse(el ? el.textContent : '{"pages":0,"products":[]}');
    };
    const first = await fetchPage(1);
    let products = first.products || [];
    for (let n = 2; n <= Math.min(first.pages || 1, 6); n += 1) {
      const more = await fetchPage(n);
      products = products.concat(more.products || []);
    }
    try { sessionStorage.setItem(key, JSON.stringify({ t: Date.now(), p: products })); } catch (e) { /* quota */ }
    return products;
  };

  let swallowKeyup = false;
  const picker = { sheet: null, opener: null, family: '', k: 0, picks: new Map(), members: [] };

  const pickTotal = () => [...picker.picks.values()].reduce((a, b) => a + b, 0);
  const slotHtml = (root, key) => {
    const tpl = root.querySelector('[data-soi-slot-tpl]');
    const id = `IMG-PDP-${key || 'PRODUKT'}-G01`;
    if (!tpl) return '';
    return tpl.innerHTML.split('IMG-PDP-NIDRAA-G01').join(esc(id));
  };
  const renderPickerState = () => {
    const s = picker.sheet;
    if (!s) return;
    const t = T();
    const total = pickTotal();
    const counter = s.querySelector('[data-msheet-counter]');
    if (counter) {
      counter.textContent = fill(t.counter, { n: total, total: picker.k });
      counter.classList.toggle('is-complete', total === picker.k);
    }
    const add = s.querySelector('[data-msheet-add]');
    const label = s.querySelector('[data-msheet-add-label]');
    if (label) label.textContent = picker.k === 1 ? t.ctaOne : fill(t.ctaMany, { count: picker.k });
    if (add) {
      const ok = total === picker.k;
      add.disabled = !ok;
      add.setAttribute('aria-disabled', ok ? 'false' : 'true');
    }
    s.querySelectorAll('[data-mp-row]').forEach((row) => {
      const v = row.dataset.v;
      const q = picker.picks.get(v) || 0;
      const out = row.querySelector('[data-mp-val]');
      if (out) out.textContent = q;
      const minus = row.querySelector('[data-mp-step="-1"]');
      const plus = row.querySelector('[data-mp-step="1"]');
      if (minus) minus.disabled = q <= 0;
      if (plus) plus.disabled = total >= picker.k;
      row.classList.toggle('is-on', q > 0);
    });
  };
  // hyphenated words („Schlaf-Roll-on", „Ylang-Ylang") never break at their hyphens (A7-R3-02)
  const keepWords = (name) => esc(name).split(' ').map((w) => (w.includes('-') ? `<span class="soi-nowrap">${w}</span>` : w)).join(' ');
  const renderRows = (root) => {
    const s = picker.sheet;
    const list = s.querySelector('[data-msheet-list]');
    const t = T();
    const inCart = (root.dataset.variants || ',');
    list.innerHTML = picker.members.map((p) => {
      const v = String(p.v);
      const up = p.ml ? fill(t.unitPrice, { price: money(unitCents(p.p, p.ml)) }) : '';
      const upSr = p.ml ? fill(t.unitPriceSr, { price: money(unitCents(p.p, p.ml)) }) : '';
      const thumb = p.img
        ? `<span class="soi-slot soi-slot--image" style="aspect-ratio:1 / 1"><img class="soi-slot__img" src="${esc(p.img)}" alt="" width="96" height="96" loading="lazy"></span>`
        : slotHtml(root, p.k);
      const isIn = inCart.includes(`,${v},`);
      const controls = p.a
        ? `<div class="soi-stepper soi-mprow__stepper">
            <button type="button" class="soi-stepper__btn" data-mp-step="-1" aria-label="${esc(fill(t.minus, { name: p.n }))}"><svg width="16" height="16" viewBox="0 0 256 256" fill="currentColor" aria-hidden="true" focusable="false"><path d="M228,128a12,12,0,0,1-12,12H40a12,12,0,0,1,0-24H216A12,12,0,0,1,228,128Z"/></svg></button>
            <output class="soi-stepper__val" data-mp-val aria-label="${esc(t.qty)}">0</output>
            <button type="button" class="soi-stepper__btn" data-mp-step="1" aria-label="${esc(fill(t.plus, { name: p.n }))}"><svg width="16" height="16" viewBox="0 0 256 256" fill="currentColor" aria-hidden="true" focusable="false"><path d="M228,128a12,12,0,0,1-12,12H140v76a12,12,0,0,1-24,0V140H40a12,12,0,0,1,0-24h76V40a12,12,0,0,1,24,0v76h76A12,12,0,0,1,228,128Z"/></svg></button>
          </div>`
        : `<span class="soi-badge soi-badge--soldout">${esc(t.soldOut)}</span>`;
      return `<li class="soi-mprow${p.a ? '' : ' is-soldout'}" data-mp-row data-v="${v}">
        <span class="soi-mprow__thumb" aria-hidden="true">${thumb}</span>
        <p class="soi-mprow__name">${keepWords(p.n)}${isIn ? ` <span class="soi-mprow__in">${esc(t.inCart)}</span>` : ''}</p>
        ${p.l ? `<p class="soi-mprow__line">${esc(p.l)}</p>` : ''}
        <p class="soi-mprow__price" data-soi="price" data-price="${p.p}" data-ml="${p.ml}"><span class="soi-price__now">${money(p.p)}</span>${up ? ` <span class="soi-mprow__unit"><span aria-hidden="true">· </span><span class="soi-price__unit" data-soi="unit-price"><span aria-hidden="true">${esc(up)}</span><span class="soi-sr">${esc(upSr)}</span></span></span>` : ''}</p>
        ${controls}
      </li>`;
    }).join('');
    list.setAttribute('aria-busy', 'false');
    renderPickerState();
  };

  const sheetFocusables = (s) => [...s.querySelectorAll('button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])')]
    .filter((el) => el.offsetParent !== null || el === doc.activeElement);

  const openPicker = async (nudge) => {
    const root = nudge.closest('[data-soi-cart]');
    const sheet = root && root.querySelector('[data-soi-msheet]');
    if (!sheet) return;
    const t = T();
    picker.sheet = sheet;
    picker.opener = nudge.querySelector('[data-soi-nudge-open]') || nudge;
    picker.family = nudge.dataset.family;
    picker.k = Number(nudge.dataset.k) || 1;
    picker.picks = new Map();
    picker.members = [];
    if (nudge.dataset.soiNeed) sheet.setAttribute('data-soi-need', nudge.dataset.soiNeed);
    sheet.querySelector('[data-msheet-title]').textContent = nudge.dataset.title || '';
    const err = sheet.querySelector('[data-msheet-error]');
    if (err) err.hidden = true;
    renderPickerState();

    if (sheet.tagName === 'DIALOG') {
      if (!sheet.open) sheet.showModal();
    } else {
      sheet.hidden = false;
      const panel = root.closest('.drawer__inner') || root;
      [...root.children].forEach((c) => { if (c !== sheet && !c.matches('template')) c.inert = true; });
      requestAnimationFrame(() => sheet.classList.add('is-open'));
      panel.classList.add('has-sheet');
    }
    const close = sheet.querySelector('.soi-msheet__close');
    if (close) close.focus({ preventScroll: true });

    try {
      const products = await loadData();
      picker.members = products.filter((p) => p.f === picker.family).sort((a, b) => (a.s - b.s) || a.n.localeCompare(b.n, 'de'));
      if (picker.sheet !== sheet) return;
      renderRows(root);
    } catch (e) {
      if (err) { err.textContent = t.errorAdd || ''; err.hidden = false; }
      sheet.querySelector('[data-msheet-list]').setAttribute('aria-busy', 'false');
    }
  };

  const closePicker = (returnFocus = true) => {
    const sheet = picker.sheet;
    if (!sheet) return;
    picker.sheet = null;
    if (sheet.tagName === 'DIALOG') {
      if (sheet.open) sheet.close();
    } else {
      const root = sheet.closest('[data-soi-cart]');
      sheet.classList.remove('is-open');
      if (root) [...root.children].forEach((c) => { c.inert = false; });
      const panel = root && (root.closest('.drawer__inner') || root);
      if (panel) panel.classList.remove('has-sheet');
      const done = () => { if (!sheet.classList.contains('is-open')) sheet.hidden = true; };
      if (reduceMotion()) done(); else setTimeout(done, 260);
    }
    if (returnFocus && picker.opener && picker.opener.isConnected) {
      const drawer = drawerEl();
      if (drawer && drawer.contains(picker.opener) && typeof window.trapFocus === 'function') {
        window.trapFocus(doc.getElementById('CartDrawer'), picker.opener);
      } else {
        picker.opener.focus({ preventScroll: true });
      }
    }
  };

  const addPicks = async () => {
    const sheet = picker.sheet;
    if (!sheet || pickTotal() !== picker.k) return;
    const btn = sheet.querySelector('[data-msheet-add]');
    if (btn.getAttribute('aria-busy') === 'true') return;
    const label = sheet.querySelector('[data-msheet-add-label]');
    const before = label ? label.textContent : '';
    btn.style.minInlineSize = `${btn.offsetWidth}px`;
    btn.setAttribute('aria-busy', 'true');
    btn.setAttribute('aria-disabled', 'true');
    if (label) label.textContent = T().loading || before;
    const items = [...picker.picks.entries()].filter(([, q]) => q > 0)
      .map(([v, q]) => ({ id: Number(v), quantity: q, properties: { _soi_source: 'cart:nudge' } }));
    const fam = picker.family;
    try {
      const data = await post(addUrl(), { items });
      const inSheet = sheet.tagName !== 'DIALOG' && sheet.contains(doc.activeElement);
      closePicker(false);
      // the sheet hides while the drawer re-renders: park focus on the drawer's close button so it never drops to <body>
      if (inSheet) { const c = doc.querySelector('#CartDrawer [data-soi-cart-close]'); if (c) c.focus({ preventScroll: true }); }
      await applySections(data.sections, { focus: false });
      publishUpdate(data);
      const scope = drawerEl() && drawerEl().classList.contains('active') ? doc.getElementById('CartDrawer') : pageHost();
      const target = scope && (scope.querySelector(`[data-soi-group][data-family="${fam}"] .soi-cgroup__title`) || scope.querySelector('[data-soi-cart-close], .soi-cpage__title'));
      if (target) {
        if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
        restoreFocus(scope, null, target);
      }
    } catch (err) {
      btn.removeAttribute('aria-busy');
      btn.style.minInlineSize = '';
      if (label) label.textContent = before;
      renderPickerState();
      const e = sheet.querySelector('[data-msheet-error]');
      if (e) { e.textContent = errorText(err) || T().errorAdd; e.hidden = false; }
    }
  };

  /* ---------------------------------------------------------------- delegated events */
  doc.addEventListener('click', (e) => {
    const t = e.target;
    const step = t.closest('[data-soi-qty] [data-step]');
    if (step) { e.preventDefault(); onStep(step); return; }
    const setStep = t.closest('[data-soi-setqty] [data-step]');
    if (setStep) { e.preventDefault(); onSetStep(setStep); return; }
    const nudgeBtn = t.closest('[data-soi-nudge-open]');
    if (nudgeBtn) { e.preventDefault(); openPicker(nudgeBtn.closest('[data-soi-nudge]')); return; }
    const mpStep = t.closest('[data-mp-step]');
    if (mpStep && picker.sheet) {
      const row = mpStep.closest('[data-mp-row]');
      const v = row.dataset.v;
      const q = picker.picks.get(v) || 0;
      const d = Number(mpStep.dataset.mpStep);
      if (d > 0 && pickTotal() >= picker.k) return;
      picker.picks.set(v, Math.max(0, q + d));
      renderPickerState();
      if (mpStep.disabled) {
        const other = row.querySelector(`[data-mp-step="${-d}"]`);
        if (other && !other.disabled) other.focus();
        else { const add = picker.sheet.querySelector('[data-msheet-add]'); if (add && !add.disabled) add.focus(); }
      }
      return;
    }
    if (t.closest('[data-msheet-add]')) { e.preventDefault(); addPicks(); return; }
    if (t.closest('[data-msheet-close]')) { e.preventDefault(); closePicker(); }
  });

  doc.addEventListener('submit', async (e) => {
    const form = e.target;
    if (form.matches('[data-soi-qty]')) { e.preventDefault(); return; }
    if (form.closest('[data-soi-setqty]')) { e.preventDefault(); return; }
    if (form.matches('[data-soi-remove-group]')) { e.preventDefault(); confirmGroup(form); return; }
    if (form.matches('[data-soi-remove]')) {
      e.preventDefault();
      try { await runUpdate(formUpdates(form), form); } catch (err) { showError(form.closest('[data-soi-row]') || form.closest('.soi-cgroup'), errorText(err)); }
      return;
    }
    if (form.matches('[data-soi-bump]')) {
      e.preventDefault();
      const btn = form.querySelector('button');
      if (btn && btn.getAttribute('aria-busy') === 'true') return;
      const label = btn && btn.querySelector('.soi-btn__label');
      const before = label ? label.textContent : '';
      // busy, not disabled: a disabled button would drop keyboard focus to <body> while the request runs
      if (btn) { btn.style.minInlineSize = `${btn.offsetWidth}px`; btn.setAttribute('aria-busy', 'true'); btn.setAttribute('aria-disabled', 'true'); if (label) label.textContent = T().loading || before; }
      const fd = new FormData(form);
      try {
        const vid = String(fd.get('id') || '');
        const inDrawer = !!form.closest('#CartDrawer');
        const data = await post(addUrl(), { items: [{ id: Number(vid), quantity: 1, properties: { _soi_source: 'cart:bump' } }] });
        await applySections(data.sections, { focus: false });
        publishUpdate(data);
        // the bump card is gone: focus the new nudge button, else the „+" of the bumped row, else close / page title
        const scope = inDrawer ? doc.getElementById('CartDrawer') : pageHost();
        if (scope) {
          const plus = vid ? scope.querySelector(`[data-soi-fid="plus-${CSS.escape(vid)}"]:not([disabled])`) : null;
          restoreFocus(scope, 'nudge', plus || scope.querySelector('[data-soi-cart-close], .soi-cpage__title'));
        }
      } catch (err) {
        if (btn) { btn.removeAttribute('aria-busy'); btn.removeAttribute('aria-disabled'); btn.style.minInlineSize = ''; if (label) label.textContent = before; }
        showError(form.closest('.soi-cbump'), errorText(err));
      }
      return;
    }
    if (form.matches('[data-soi-checkout]')) {
      const btn = form.querySelector('[name="checkout"]');
      if (btn) { btn.classList.add('is-loading'); btn.setAttribute('aria-busy', 'true'); }
    }
  });

  // Esc inside the sheet closes the sheet only (Studio closes the drawer on keyup Escape)
  // Focus trap for the open drawer (Studio's trap keeps stale first/last nodes after a re-render)
  doc.addEventListener('keydown', (e) => {
    if (e.key !== 'Tab' || picker.sheet) return;
    const d = drawerEl();
    if (!d || !d.classList.contains('active')) return;
    const panel = d.querySelector('.drawer__inner');
    if (!panel) return;
    const f = sheetFocusables(panel).filter((el) => !el.closest('[inert], [hidden]'));
    if (!f.length) return;
    const first = f[0];
    const last = f[f.length - 1];
    const inside = panel.contains(doc.activeElement);
    if (!inside) { e.preventDefault(); e.stopPropagation(); first.focus(); return; }
    if (e.shiftKey && doc.activeElement === first) { e.preventDefault(); e.stopPropagation(); last.focus(); }
    else if (!e.shiftKey && doc.activeElement === last) { e.preventDefault(); e.stopPropagation(); first.focus(); }
  }, true);
  doc.addEventListener('keydown', (e) => {
    if (!picker.sheet) return;
    if (e.key === 'Escape' && picker.sheet.tagName !== 'DIALOG') {
      e.preventDefault();
      e.stopPropagation();
      swallowKeyup = true;
      closePicker();
      return;
    }
    if (e.key === 'Tab' && picker.sheet.tagName !== 'DIALOG' && picker.sheet.contains(e.target)) {
      const f = sheetFocusables(picker.sheet);
      if (!f.length) return;
      const first = f[0];
      const last = f[f.length - 1];
      if (e.shiftKey && doc.activeElement === first) { e.preventDefault(); e.stopPropagation(); last.focus(); }
      else if (!e.shiftKey && doc.activeElement === last) { e.preventDefault(); e.stopPropagation(); first.focus(); }
    }
  }, true);
  // Esc closes the drawer from anywhere while it is open (Studio only listens to keyup inside the element)
  doc.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape' || picker.sheet || swallowKeyup) return;
    const d = drawerEl();
    if (!d || !d.classList.contains('active') || d.contains(e.target)) return;
    if (doc.querySelector('dialog[open]')) return;
    d.close();
  });
  doc.addEventListener('close', (e) => {
    if (picker.sheet && e.target === picker.sheet) { picker.sheet = null; if (picker.opener && picker.opener.isConnected) picker.opener.focus(); }
  }, true);

  /* ---------------------------------------------------------------- Studio <cart-drawer> extension */
  let pushed = false;
  let fromPop = false;
  const onPop = () => {
    const d = drawerEl();
    if (pushed && d && d.classList.contains('active')) { fromPop = true; pushed = false; d.close(); fromPop = false; }
  };
  window.addEventListener('popstate', onPop);

  // after an add on the cart page: the added family's group title, else the added row's name, else the page title
  const focusPageAdd = (vid) => {
    const host = pageHost();
    if (!host) return;
    const row = vid ? host.querySelector(`[data-soi-row][data-variant="${CSS.escape(vid)}"]`) : null;
    const group = row && row.closest('.soi-cgroup');
    const el = (group && group.querySelector('.soi-cgroup__title'))
      || (row && row.querySelector('a.soi-crow__name'))
      || host.querySelector('.soi-cpage__title');
    if (!el) return;
    if (!el.matches('a[href], button') && !el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1');
    el.focus({ preventScroll: true });
    el.scrollIntoView({ block: 'center', behavior: reduceMotion() ? 'auto' : 'smooth' });
  };

  const patchDrawer = (C) => {
    const proto = C.prototype;
    if (proto.__soiPatched) return;
    proto.__soiPatched = true;
    const open0 = proto.open;
    const close0 = proto.close;
    const render0 = proto.renderContents;
    // initial focus is always the close button (05 C32, R3-G-10): a second Enter after a keyboard add must not
    // reach „Zur Kasse"
    const openFocus = (d) => {
      d.__soiFromAdd = false;
      return d.querySelector('[data-soi-cart-close]') || d.querySelector('.drawer__close');
    };
    proto.open = function open(trigger) {
      if (this.classList.contains('active')) {
        injectExpress(this);
        restoreFocus(doc.getElementById('CartDrawer'), null, openFocus(this));
        return;
      }
      if (!trigger && !this.activeElement && doc.activeElement && doc.activeElement !== doc.body && !this.contains(doc.activeElement)) {
        this.setActiveElement(doc.activeElement);
      }
      open0.call(this, trigger);
      injectExpress(this);
      const target = openFocus(this);
      let done = false;
      const focusIn = () => {
        if (done) return;
        done = true;
        const el = target && target.isConnected ? target : this.querySelector('[data-soi-cart-close]');
        if (el) restoreFocus(doc.getElementById('CartDrawer'), null, el);
      };
      this.addEventListener('transitionend', focusIn, { once: true });
      if (small() && !pushed) {
        try { history.pushState({ soiDrawer: true }, ''); pushed = true; } catch (e) { pushed = false; }
      }
      afterRender({ announce: false });
    };
    proto.close = function close() {
      if (picker.sheet && this.contains(picker.sheet)) closePicker(false);
      close0.call(this);
      if (pushed && !fromPop) {
        pushed = false;
        if (history.state && history.state.soiDrawer) history.back();
      }
    };
    proto.renderContents = function renderContents(parsedState) {
      // renderContents is the add path (buy box ATC, quick add, picker, bump outside the drawer)
      // On the cart page the page itself shows the new state: no drawer over it (A7-R3-06)
      const host = pageHost();
      if (host) {
        const vid = parsedState && parsedState.id != null ? String(parsedState.id) : '';
        refreshAll({ announce: true, focus: false }).then(() => focusPageAdd(vid));
        return;
      }
      this.__soiFromAdd = true;
      if (parsedState && parsedState.sections && foreign(parsedState.sections)) {
        refreshAll({ announce: true, focus: false }).then(() => this.open());
        return;
      }
      const root = doc.getElementById('CartDrawer');
      const prev = barValue(root);
      render0.call(this, parsedState);
      animateBar(doc.getElementById('CartDrawer'), prev);
      afterRender({ announce: true });
    };
  };
  if (window.customElements) {
    customElements.whenDefined('cart-drawer').then(() => patchDrawer(customElements.get('cart-drawer')));
    customElements.whenDefined('cart-drawer-items').then(() => {
      const C = customElements.get('cart-drawer-items');
      if (C && !C.prototype.__soiPatched) {
        C.prototype.__soiPatched = true;
        C.prototype.onCartUpdate = function onCartUpdate() { return refreshAll({ announce: false, focus: false }); };
      }
    });
  }

  // swallow the keyup that follows an Escape handled by the sheet or the confirm row
  const guardKeyup = (d) => {
    if (!d || d.__soiKeyGuard) return;
    d.__soiKeyGuard = true;
    d.addEventListener('keyup', (e) => {
      if (e.key === 'Escape' && (swallowKeyup || picker.sheet)) { e.stopPropagation(); swallowKeyup = false; }
    }, true);
  };

  window.soiCart = { refresh: () => refreshAll({ announce: false, focus: false }), applySections, money };

  const boot = () => {
    guardKeyup(drawerEl());
    afterRender({ announce: false });
  };
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
